import Player from '@vimeo/player';

export default class VideoPlayerActivator {
  constructor(selector = '.video-player') {
    this.valid = true;
    this.players = [...document.querySelectorAll(selector)];

    if (!this.players.length) {
      this.valid = false;
      return;
    }

    this.mute = true;
    this.vimeoInstances = new Map();
    this.boundHandlers = new Map();
    this.fullscreenChangeHandler = this.handleFullscreenChange.bind(this);
    this.orientationChangeHandler = this.handleOrientationChange.bind(this);
    this.videoPlayer = document.querySelector('.video-player');
    this.player = false;
    this.orientationLock = false;
    this.lastOrientation = null;
    this.activeContainer = null;
    this.controlsTimeout = null;
    this.count = 1;

    this.init();
    this.fullScale = true;

    const ua = navigator.userAgent;
    const vendor = navigator.vendor;

    if (
      /Android/i.test(ua) && (
        (/Chrome/i.test(ua) && /Google Inc/.test(vendor) && !/EdgA/i.test(ua)) || // Chrome
        /EdgA/i.test(ua) ||                                                       // Edge
        /SamsungBrowser/i.test(ua)                                                // Samsung Internet
      )
    ) {
      this.fullScale = false;
    }

  }

  init() {

    this.lastOrientation =  screen.orientation?.type || (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');


    // Add all fullscreen event listeners for cross-browser support
    ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange']
      .forEach(event => document.addEventListener(event, this.fullscreenChangeHandler));
    
    window.addEventListener('orientationchange', this.orientationChangeHandler);
    window.addEventListener('resize', this.orientationChangeHandler);

    this.players.forEach(player => {
      const cover = player.querySelector('.cover');
      const coverVideo = cover?.querySelector('video');
      const coverImage = cover?.querySelector('img');
      const playButton = player?.querySelector('.iframe-touch-overlay');
      const playIcon = cover?.querySelector('.play-button');
      const iframe = player.querySelector('iframe');

      if (!iframe) return;

      // Prepare iframe source
      const iframeSrc = iframe.dataset.src;
      
      // For mobile, add autoplay and muted parameters
      const separator = iframeSrc.includes('?') ? '&' : '?';
      const mobileSrc = window.mobile 
        ? `${iframeSrc}${separator}autoplay=0&muted=1&playsinline=1`
        : iframeSrc;
      
      iframe.src = mobileSrc;
      iframe.style.opacity = '0';
      
      // Allow iframe to be ready
      iframe.addEventListener('load', () => {
        this.initVimeoControls(player, iframe);
      }, { once: true });

      // Setup cover video for mobile
      if (coverVideo && coverVideo.dataset.src && !coverVideo.src) {
        // Mobile-friendly video attributes
        coverVideo.setAttribute('playsinline', '');
        coverVideo.setAttribute('webkit-playsinline', '');
        coverVideo.muted = true;
        coverVideo.playsInline = true;
        
        coverVideo.src = coverVideo.dataset.src;
        delete coverVideo.dataset.src;
        
        // Try to play with mobile fallbacks
        const playPromise = coverVideo.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // If autoplay fails, show image
            if (coverImage) coverImage.style.opacity = '1';
            // Setup click-to-play
            const playOnClick = () => {
              coverVideo.play();
              cover.removeEventListener('click', playOnClick);
            };
            cover.addEventListener('click', playOnClick);
          });
        }
      }

      if (!cover || !playButton) return;

      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Hide overlay
        if (playButton) playButton.style.display = 'none';
        
        // Fade out cover
        this.fadeOutCover(cover);
        
        // Show and enable iframe
        iframe.style.transition = 'opacity 0.6s ease';
        iframe.style.opacity = '1';
        iframe.style.pointerEvents = 'auto';

        // Play video
        const vimeoPlayer = this.vimeoInstances.get(player);

        const muteToggle = player.querySelector('.mute-toggle');
        
        if (vimeoPlayer) {
          // Ensure Vimeo player is ready before setting volume
          vimeoPlayer.ready().then(() => {
            // First unmute (in response to user interaction)
            if (this.mute) {
              vimeoPlayer.setVolume(1).then(() => {
                this.mute = false;
                if (muteToggle) muteToggle.textContent = 'MUTE';
              }).catch(err => {
                console.warn('Unmute failed:', err);
              });
            }

            // Now try to play
            return vimeoPlayer.play();
          }).catch(err => {
            showControls();
            console.error('Vimeo play error:', err);
          });
        }
      };

      // Mobile-friendly event handling
      if (window.touch || window.mobile) {
        // Use touchstart for immediate response on mobile
        playButton.addEventListener('touchstart', handler, { passive: false });
        playButton.addEventListener('click', handler);
      } else {
        playButton.addEventListener('click', handler);
      }
      
      this.boundHandlers.set(playButton, handler);
    });
  }

  handleOrientationChange() {

      if (this.orientationLock) return;

      const currentOrientation = screen.orientation?.type ||
        (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');

      // If orientation hasn't changed, do nothing
      if (this.lastOrientation === currentOrientation) return;

      this.orientationLock = true;

      setTimeout(() => {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isAndroid = /Android/i.test(navigator.userAgent);

        const isLandscape = currentOrientation.includes('landscape') || 
          window.innerWidth > window.innerHeight;

        const isPortrait = currentOrientation.includes('portrait') || 
          window.innerHeight >= window.innerWidth;

        const wrap = this.activeContainer;
        if (!wrap) return;

        if ((isIOS || isAndroid) && isLandscape && window.innerWidth <= 1024) {
          this.enterMobileFullscreen(wrap);
        } else if ((isIOS || isAndroid) && isPortrait) {
          this.exitMobileFullscreen(wrap);
        }

        this.lastOrientation = currentOrientation;
        this.orientationLock = false;
      }, 100);
    }

  enterMobileFullscreen(wrap) {
    document.body.style.backgroundColor = '#000000';
    
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    // if (metaTheme) metaTheme.setAttribute('content', '#000');

    fadeColor({
      from: '#ffffff',
      to: '#000000',
      duration: 500,
      onUpdate: (color) => {
        document.body.style.backgroundColor = color;
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', color);
      }
    });
      wrap.style.position = 'fixed';
      wrap.style.top = '0';
      wrap.style.left = '0';
      wrap.style.width = '100vw';
      wrap.style.height = '100vh';
      wrap.style.zIndex = '9999';
      wrap.style.display = 'block';
      wrap.style.opacity = '1';
      wrap.style.maxHeight = '100vh';
      wrap.style.maxHeight = '100svh';

      const top = (window.pageYOffset || document.scrollTop) - (document.clientTop || 0);

      if(top > 40 && this.fullScale){
          wrap.style.maxHeight = '100vh';        
      }

      wrap.dispatchEvent(new Event('touchstart'));

    
    // Click fullscreen toggle if exists
    const fullscreenToggle = wrap.querySelector('.fullscreen-toggle');
    if (fullscreenToggle) fullscreenToggle.click();
    
    document.body.classList.add('fullscreen-mode');
  }

  exitMobileFullscreen(wrap) {
    // Dispatch touchstart to show controls
    wrap.dispatchEvent(new Event('touchstart'));
    
    document.body.style.backgroundColor = '';
    
    const metaTheme = document.querySelector('meta[name="theme-color"]');
   
    fadeColor({
      from: '#000000',
      to: '#ffffff',
      duration: 200,
      onUpdate: (color) => {
        document.body.style.backgroundColor = color;
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', color);
      }
    });

      wrap.style.position = '';
      wrap.style.top = '';
      wrap.style.left = '';
      wrap.style.width = '';
      wrap.style.height = '';
      wrap.style.zIndex = '';
      wrap.style.display = '';
      wrap.style.opacity = '1';
      wrap.style.maxHeight = '';

    // if (document.fullscreenElement) {
    //   document.exitFullscreen().catch(e => console.log('Exit fullscreen failed:', e));
    // } else if (document.webkitFullscreenElement) {
    //   document.webkitExitFullscreen();
    // }
    
    document.body.classList.remove('fullscreen-mode');
  }

  goFullScreen() {
    const rect = this.videoPlayer.getBoundingClientRect();
    const scrollTop = window.scrollY + rect.top - (window.innerHeight / 2) + (rect.height / 2);
    document.querySelector('.fullscreen-toggle').click();
  }

  enableAutoHideControls(container) {
    const controls = container.querySelector('.controls');
    if (!controls) return;

    const hideControls = () => {
      controls.style.opacity = '0';
      controls.style.pointerEvents = 'none';
    };

    const showControls = () => {
      controls.style.opacity = '1';
      controls.style.pointerEvents = 'auto';
    };

    const resetTimer = () => {
      showControls();
      if (this.controlsTimeout) clearTimeout(this.controlsTimeout);
      this.controlsTimeout = setTimeout(hideControls, 4000);
    };

    // Mouse + touch movement on container (not just controls)
    container.addEventListener('mousemove', resetTimer);
    container.addEventListener('touchstart', resetTimer, { passive: true });

    // Start with visible controls, then fade after timeout
    resetTimer();
  }

  async initVimeoControls(container, iframe) {
    const player = new Player(iframe);

    player.pause();
    this.player = player;
    this.vimeoInstances.set(container, player);

    player.ready().then(() => {
      const playIcon = document?.querySelector('.play-button');
      if (playIcon) playIcon.style.opacity = 1;

      this.count++;
    });

    const playToggle = container.querySelector('.play-toggle');
    const muteToggle = container.querySelector('.mute-toggle');
    const fullscreenToggle = container.querySelector('.fullscreen-toggle');
    const timeDisplay = container.querySelector('.time-display');
    const slider = container.querySelector('.custom-slider');
    const fill = slider?.querySelector('.fill');
    const thumb = slider?.querySelector('.thumb');
    const track = slider?.querySelector('.track');
    const playerWrap = this.videoPlayer;

    this.activeContainer = container;
    this.enableAutoHideControls(container);

    // Update slider on time
    player.on('timeupdate', ({ seconds, duration }) => {
      const percent = (seconds / duration) * 100;
      if (fill) fill.style.width = `${percent}%`;
      if (thumb) thumb.style.left = `${percent}%`;
      if (timeDisplay) timeDisplay.textContent = this.formatTime(seconds);
    });

    player.on('play', () => {
      if (playToggle) playToggle.textContent = 'PAUSE';
    });

    // Play/Pause
    if (playToggle) {
      playToggle.addEventListener('click', async () => {
        const isPaused = await player.getPaused();
        isPaused ? player.play() : player.pause();
        playToggle.textContent = isPaused ? 'PAUSE' : 'PLAY';
      });
    }

    // Mute
    if (muteToggle) {
      muteToggle.addEventListener('click', async () => {
        const vol = await player.getVolume();
        await player.setVolume(vol === 0 ? 1 : 0);
        this.mute = vol === 0 ? false : true;
        muteToggle.textContent = vol === 0 ? 'MUTE' : 'UNMUTE';
      });
    }

    // Fullscreen
    if (fullscreenToggle) {
      fullscreenToggle.addEventListener('click', (e) => {
        e.preventDefault();

        if (!document.fullscreenElement && !document.mozFullScreenElement && 
            !document.webkitFullscreenElement && !document.msFullscreenElement) {
          
          const requestMethod = playerWrap.requestFullScreen ||
            playerWrap.webkitRequestFullscreen ||
            playerWrap.mozRequestFullScreen ||
            playerWrap.msRequestFullscreen;

          if (requestMethod) {
            requestMethod.apply(playerWrap);
            document.body.classList.add('fullscreen-mode');
          } else {
            player.requestFullscreen().catch((error) => {
              console.error("Error entering fullscreen:", error);
            });
          }
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          }
          document.body.classList.remove('fullscreen-mode');
        }
      });
    }

    // Custom Slider Logic
    if (slider && track && fill && thumb) {
      const onMove = (e) => {
        const clientX = e.touches?.[0]?.clientX ?? e.clientX;
        const rect = track.getBoundingClientRect();
        let percent = (clientX - rect.left) / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        fill.style.width = `${percent * 100}%`;
        thumb.style.left = `${percent * 100}%`;
        player.getDuration().then(duration => {
          player.setCurrentTime(percent * duration);
        });
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchend', onUp);
      };

      slider.addEventListener('mousedown', (e) => {
        onMove(e);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      });

      slider.addEventListener('touchstart', (e) => {
        e.preventDefault();
        onMove(e);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
      });
    }
  }

  fadeOutCover(cover) {
    cover.style.transition = 'opacity 0.6s ease';
    cover.style.opacity = '0';
    cover.style.pointerEvents = 'none';
    setTimeout(() => (cover.style.display = 'none'), 600);
  }

  destroy() {
    console.log('Destroy called: cleaning up VideoPlayerActivator');
    
    // Clear timeouts
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
      this.controlsTimeout = null;
    }

    // Remove handlers
    this.boundHandlers.forEach((handler, btn) => {
      btn.removeEventListener('click', handler);
      btn.removeEventListener('touchstart', handler);
    });
    this.boundHandlers.clear();

    // Remove fullscreen listeners
    ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange']
      .forEach(event => document.removeEventListener(event, this.fullscreenChangeHandler));
    
    window.removeEventListener('orientationchange', this.orientationChangeHandler);
    window.removeEventListener('resize', this.orientationChangeHandler);

    // Unload Vimeo instances
    this.vimeoInstances.forEach(player => {
      player.unload();
    });
    this.vimeoInstances.clear();
    
    // Clear references
    this.players = [];
    this.activeContainer = null;
    this.player = null;
    this.videoPlayer = null;
    
    this.valid = false;
  }

  handleFullscreenChange() {
    const isFullscreen = !!(document.fullscreenElement || 
      document.webkitFullscreenElement || 
      document.mozFullScreenElement || 
      document.msFullscreenElement);
    
    const container = this.activeContainer;
    if (!container) return;

    const fsToggle = container.querySelector('.fullscreen-toggle');
    if (isFullscreen) {
      container.classList.add('is-fullscreen');
      if (fsToggle) fsToggle.textContent = 'EXIT';
    } else {
      container.classList.remove('is-fullscreen');
      if (fsToggle) fsToggle.textContent = 'FULLSCREEN';
    }
  }

  formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const h = hrs > 0 ? String(hrs).padStart(2, '0') + ':' : '';
    const m = String(mins).padStart(2, '0');
    const s = String(secs).padStart(2, '0');

    return `${h}${m}:${s}`;
  }
}

function fadeColor({
  from = '#ffffff',
  to = '#000000',
  duration = 600,
  easing = t => 1 - Math.pow(1 - t, 3), // ease-out cubic
  onUpdate = () => {}
}) {
  const hexToRgb = (hex) => {
    const bigint = parseInt(hex.replace('#', ''), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  };

  const fromRGB = hexToRgb(from);
  const toRGB = hexToRgb(to);
  const startTime = performance.now();

  function step(now) {
    const rawProgress = Math.min((now - startTime) / duration, 1);
    const progress = easing(rawProgress);

    const r = Math.round(fromRGB.r + (toRGB.r - fromRGB.r) * progress);
    const g = Math.round(fromRGB.g + (toRGB.g - fromRGB.g) * progress);
    const b = Math.round(fromRGB.b + (toRGB.b - fromRGB.b) * progress);

    const color = `rgb(${r}, ${g}, ${b})`;
    onUpdate(color);

    if (rawProgress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}


function isSamsungDevice() {
  console.log(navigator.userAgent);
  return /SM-|GT-|SCH-|SHV-|SHW-|SPH-|SGH-/i.test(navigator.userAgent);
}

function isSamsungModern() {
  if (!navigator.userAgentData) return false;
  return navigator.userAgentData.getHighEntropyValues(["model"])
    .then(data => /SM-|Samsung/i.test(data.model));
}

function isChromeOnAndroid() {
  const ua = navigator.userAgent;
  return /Android/i.test(ua) &&
         /Chrome/i.test(ua) &&
         /Google Inc/.test(navigator.vendor) &&
         !/EdgA/i.test(ua); // exclude Edge
}

function isEdgeOnAndroid() {
  return /Android/i.test(navigator.userAgent) && /EdgA/i.test(navigator.userAgent);
}

function isSamsungInternet() {
  return /SamsungBrowser/i.test(navigator.userAgent);
}